import { useState, useEffect } from "react";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import Modal from "../../components/Modal";
import classes from "../../components/Modal.module.css";
import ErrorMessage from "../../components/ErrorMessage";
import saveIcon from "../../assets/save_icon.png";
import CustomInput from "../../components/CustomInput";
import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { onlyNums } from "../../utils/OnlyNums";
import Textarea from "../../components/Textarea";
import ErrorBox from "../../components/ErrorBox";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import AreYouSureModal from "../../components/AreYouSureModal";
import deleteIcon from "../../assets/delete_Icon.png";
import closeIcon from "../../assets/close_icon.png";
import axiosClient from "../../services/axiosClient";

export default function UpdateDeductionsModal({
  deduction,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation("general");
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    price: 0,
    notes: "",
  });

  useEffect(() => {
    if (deduction) {
      setFormData({
        name: deduction.name || "",
        price: deduction.price || 0,
        notes: deduction.notes || "",
      });
    }
  }, [deduction]);

  const handleCancel = () => {
    onClose();
  };

  const handleDeductionUpdate = async () => {
    if (!formData.name.trim()) {
      setValidationError("noDeductionName");
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      setValidationError("invalidDeductionValue");
      return;
    }

    try {
      setLoadingModal(true);
      setError(null);

      const response = await axiosClient.put(`/deductions/updateDeduction`, {
        id: deduction.id,
        name: formData.name,
        price: formData.price,
        notes: formData.notes,
      });

      if (response.status === 200) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error updating deduction:", err);
      setError("deductionUpdateFailed");
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <>
      <Modal
        title="updateDeduction"
        onClose={handleCancel}
        modalSize={"c-modal--small"}
        footer={
          <>
            {loadingModal ? <Loader size={LOADER_SIZES.MEDIUM} /> : null}
            {error ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(error)}
              </ErrorMessage>
            ) : null}
            {validationError ? (
              <ErrorMessage classList="o-row o-row--horizontal-align_end">
                {t(validationError)}
              </ErrorMessage>
            ) : null}
            <section className={classes.actions}>
              <CustomButton
                onClick={handleDeductionUpdate}
                title={t("confirm")}
                icon={saveIcon}
                iconSize={16}
                disabled={loadingModal}
              />
              <CustomButton
                onClick={() => setShowDeleteModal(true)}
                title={t("delete")}
                icon={deleteIcon}
                iconSize={16}
                disabled={loadingModal}
                variant="red"
              />
              <CustomButton
                title={t("cancel")}
                onClick={handleCancel}
                variant="blank"
                icon={closeIcon}
                iconSize={16}
              />
            </section>
          </>
        }
      >
        {error ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(error)}
          </ErrorBox>
        ) : null}
        {validationError ? (
          <ErrorBox classList="o-row o-row--horizontal-align_end">
            {t(validationError)}
          </ErrorBox>
        ) : null}
        <div className="o-column o-column--gap-size_400">
          <CustomInput
            title={t("deductionName")}
            name="deductionName"
            value={formData.name}
            onChange={(e) => {
              if (validationError === "noDeductionName") {
                setValidationError(null);
              }
              setFormData((prev) => ({
                ...prev,
                name: e.target.value,
              }));
            }}
            flex={true}
            errorText={
              validationError === "noDeductionName" ? t("noDeductionName") : ""
            }
          />

          <CustomInputWithUnit
            name="price"
            title={t("price")}
            value={formData.price}
            onChange={(e) => {
              if (validationError === "invalidEntitlementValue") {
                setValidationError(null);
              }
              setFormData((prev) => ({
                ...prev,
                price: e.target.value,
              }));
            }}
            onKeyDown={onlyNums}
            flex={true}
            min={0}
            max={100}
            unit="IQD"
            type="moneyV2"
            errorText={
              validationError === "invalidEntitlementValue"
                ? t("invalidEntitlementValue")
                : ""
            }
          />

          <div className="">
            <label>{t("notes")}</label>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }));
              }}
              flex={true}
            />
          </div>
        </div>
      </Modal>

      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          deduction={deduction}
          onSuccess={() => {
            setShowDeleteModal(false);
            onSuccess();
          }}
        />
      )}
    </>
  );
}

function DeleteModal({ setShowDeleteModal, deduction, onSuccess }) {
  const [loadingModal, setLoadingModal] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation("general");

  const handleDeleteDeduction = async () => {
    try {
      setLoadingModal(true);
      setError(null);

      await axiosClient.delete(`/deductions/deleteDeduction`, {
        data: {
          id: deduction.id,
        },
      });

      onSuccess();
    } catch (err) {
      console.error("Error deleting entitlement:", err);
      setError("deductionDeleteFailed");
    } finally {
      setLoadingModal(false);
    }
  };
  return (
    <AreYouSureModal
      onClose={() => setShowDeleteModal(false)}
      onConfirm={handleDeleteDeduction}
      customMessage="areYouSureToDeleteDeduction"
      closeTitle="cancel"
      confirmTitle="delete"
      loading={loadingModal}
      confirmButtonVariant="red"
      error={t(error)}
    />
  );
}
